import { MiniAppBlueprint, ChatMessage, AiNetworkLog, PollinationsConfig } from './types';

const SYSTEM_PROMPT = `You are the Neural Mini-App Architect, an expert AI engineer that builds high-conversion Telegram Mini Apps (TMA) for earnings, rewards, crypto tasks, affiliate recruitment, and web3 staking.

Your goal is to converse with the user and design/update their custom Telegram Mini App blueprint.

Available Template Bases:
- 'hybrid_rewards_x': Crypto rewards with daily lucky spin, task cards, balance charts, and wallet clearance.
- 'earnhub': High performance engagement & recruitment grid with mission audits, recruit trees, and multi-channel payouts.
- 'sparkbnb': Staking & claim rewards portal with daily claiming streaks, leaderboards, and gold/blue web3 aesthetic.
- 'duxbux': Carbon dark modern ecosystem featuring check-in streaks, monetization ad triggers, and code verification redemptions.

Available Icons: 'zap', 'flame', 'star', 'crown', 'shield', 'target', 'diamond', 'hexagon', 'rocket', 'ghost', 'cpu'.
Available Fonts: 'Outfit', 'Inter', 'Playfair Display', 'Space Grotesk', 'JetBrains Mono'.

On EVERY response, you MUST provide:
1. A concise, enthusiastic conversational explanation of the mini-app concept, economics, and changes made.
2. A JSON code block with the complete, updated blueprint structure surrounded by \`\`\`json and \`\`\`.

The JSON structure MUST strictly adhere to:
{
  "name": "App Name",
  "currency_name": "COIN/USDT/TON/POINTS",
  "template_id": "hybrid_rewards_x" | "earnhub" | "sparkbnb" | "duxbux",
  "icon": "zap" | "flame" | "star" | "crown" | "shield" | "target" | "diamond" | "hexagon" | "rocket" | "ghost" | "cpu",
  "welcome_bonus": 100,
  "referral_reward": 50,
  "theme": {
    "primaryColor": "#hex",
    "secondaryColor": "#hex",
    "accentColor": "#hex",
    "bgColor": "#hex",
    "cardColor": "rgba(255, 255, 255, 0.08)",
    "fontFamily": "Outfit" | "Inter" | "Playfair Display" | "Space Grotesk" | "JetBrains Mono",
    "loadingGradient": "linear-gradient(135deg, #hex1 0%, #hex2 100%)"
  },
  "loading_config": {
    "text": "Synchronizing Neural Core...",
    "color": "#hex"
  },
  "conversion_enabled": true,
  "conversion_rate": 0.001,
  "conversion_currency": "USDT",
  "min_withdrawal": 500,
  "mandatory_tasks_to_withdraw": 1,
  "tabs_config": [
    { "id": "home", "enabled": true, "label": "Home" },
    { "id": "tasks", "enabled": true, "label": "Tasks" },
    { "id": "spin", "enabled": true, "label": "Spin" },
    { "id": "refer", "enabled": true, "label": "Recruits" },
    { "id": "wallet", "enabled": true, "label": "Wallet" },
    { "id": "profile", "enabled": true, "label": "Profile" }
  ],
  "initial_tasks": [
    {
      "title": "Join Official Telegram Channel",
      "description": "Subscribe to our main telegram updates channel for rewards",
      "reward": 50,
      "link": "https://t.me/telegram",
      "icon": "⭐",
      "category": "Social"
    },
    {
      "title": "Follow Twitter / X Announcements",
      "description": "Follow our X profile and retweet the pinned airdrop post",
      "reward": 75,
      "link": "https://x.com",
      "icon": "⚡",
      "category": "Social"
    },
    {
      "title": "Watch Community Video Guide",
      "description": "Watch the complete onboarding video to learn how to withdraw",
      "reward": 100,
      "link": "https://youtube.com",
      "icon": "🎬",
      "category": "Missions"
    }
  ]
}

Ensure the color schemes look cohesive, modern, and high contrast. When the user asks for changes, modify only what is requested while keeping other fields valid.`;

// Free model candidates supported by Pollinations without requiring paid credits
const FREE_MODEL_CANDIDATES = ['mistral', 'deepseek', 'qwen'];

export class AiArchitectService {
  private history: ChatMessage[] = [];
  private currentBlueprint: MiniAppBlueprint | null = null;
  private logs: AiNetworkLog[] = [];
  private logListeners: Array<(logs: AiNetworkLog[]) => void> = [];
  private config: PollinationsConfig = {
    preferredModel: '',
    maxRetries: 2,
    timeoutMs: 25000
  };

  constructor() {
    this.loadSavedConfig();
    this.resetContext();
  }

  private loadSavedConfig() {
    try {
      const saved = localStorage.getItem('nexus_pollinations_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Clear any old forced models so users get the default working cluster
        if (parsed.preferredModel === 'openai-fast' || parsed.preferredModel === 'mistral') {
          parsed.preferredModel = '';
        }
        this.config = { ...this.config, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load saved Pollinations config:', e);
    }
  }

  saveConfig(newConfig: Partial<PollinationsConfig>) {
    this.config = { ...this.config, ...newConfig };
    try {
      localStorage.setItem('nexus_pollinations_config', JSON.stringify(this.config));
    } catch (e) {
      console.warn('Failed to persist Pollinations config:', e);
    }
  }

  getConfig(): PollinationsConfig {
    return { ...this.config };
  }

  onLogsChange(listener: (logs: AiNetworkLog[]) => void) {
    this.logListeners.push(listener);
    return () => {
      this.logListeners = this.logListeners.filter(l => l !== listener);
    };
  }

  getLogs(): AiNetworkLog[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    this.notifyLogs();
  }

  private addLog(log: Omit<AiNetworkLog, 'id'>): AiNetworkLog {
    const fullLog: AiNetworkLog = {
      ...log,
      id: 'log_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7)
    };
    this.logs.unshift(fullLog);
    if (this.logs.length > 50) this.logs.pop();
    this.notifyLogs();
    return fullLog;
  }

  private updateLog(id: string, updates: Partial<AiNetworkLog>) {
    const idx = this.logs.findIndex(l => l.id === id);
    if (idx !== -1) {
      this.logs[idx] = { ...this.logs[idx], ...updates };
      this.notifyLogs();
    }
  }

  private notifyLogs() {
    this.logListeners.forEach(l => l([...this.logs]));
  }

  resetContext() {
    this.history = [];
    this.currentBlueprint = this.getDefaultBlueprint();
  }

  getCurrentBlueprint(): MiniAppBlueprint {
    if (!this.currentBlueprint) {
      this.currentBlueprint = this.getDefaultBlueprint();
    }
    return this.currentBlueprint;
  }

  getHistory(): ChatMessage[] {
    return this.history;
  }

  private getDefaultBlueprint(): MiniAppBlueprint {
    return {
      name: 'CyberEarn X',
      currency_name: 'CREDITS',
      template_id: 'hybrid_rewards_x',
      icon: 'zap',
      welcome_bonus: 100,
      referral_reward: 50,
      theme: {
        primaryColor: '#6366f1',
        secondaryColor: '#a855f7',
        accentColor: '#f43f5e',
        bgColor: '#0b0f19',
        cardColor: 'rgba(255, 255, 255, 0.05)',
        fontFamily: 'Outfit',
        loadingGradient: 'linear-gradient(135deg, #0b0f19 0%, #1e1b4b 100%)'
      },
      loading_config: {
        text: 'Synchronizing Neural Link...',
        color: '#6366f1'
      },
      conversion_enabled: true,
      conversion_rate: 0.001,
      conversion_currency: 'USDT',
      min_withdrawal: 500,
      mandatory_tasks_to_withdraw: 1,
      tabs_config: [
        { id: 'home', enabled: true, label: 'Home' },
        { id: 'tasks', enabled: true, label: 'Tasks' },
        { id: 'spin', enabled: true, label: 'Spin' },
        { id: 'refer', enabled: true, label: 'Recruits' },
        { id: 'wallet', enabled: true, label: 'Wallet' },
        { id: 'profile', enabled: true, label: 'Profile' }
      ],
      initial_tasks: [
        {
          title: 'Join Official Telegram Community',
          description: 'Join the community channel and get 50 bonus coins instantly',
          reward: 50,
          link: 'https://t.me/telegram',
          icon: '⚡',
          category: 'Telegram'
        },
        {
          title: 'Follow Official X (Twitter)',
          description: 'Follow and retweet our latest announcement pin',
          reward: 75,
          link: 'https://x.com',
          icon: '🔥',
          category: 'Social'
        },
        {
          title: 'Invite 3 Friends to Mini-App',
          description: 'Share your personal referral link with 3 colleagues',
          reward: 150,
          link: '',
          icon: '👑',
          category: 'Referral'
        }
      ]
    };
  }

  // Diagnostic ping connection tester
  async testConnection(): Promise<{ ok: boolean; latencyMs: number; error?: string; details?: string }> {
    const startTime = performance.now();
    const endpoint = 'https://text.pollinations.ai/ping';
    const log = this.addLog({
      timestamp: Date.now(),
      endpoint,
      method: 'PING',
      status: 'pending',
      stage: 'Diagnostics Health Ping',
      payloadSummary: 'Testing Pollinations cluster latency'
    });

    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 10000);
      const res = await fetch(endpoint, {
        method: 'GET',
        signal: controller.signal
      });
      clearTimeout(id);
      const latencyMs = Math.round(performance.now() - startTime);

      if (res.ok) {
        this.updateLog(log.id, {
          status: 'success',
          httpStatus: res.status,
          statusText: res.statusText,
          durationMs: latencyMs,
          responseSnippet: 'Cluster responding successfully'
        });
        return { ok: true, latencyMs };
      } else {
        const text = await res.text();
        const reason = this.analyzeHttpFailure(res.status, text);
        this.updateLog(log.id, {
          status: 'failed',
          httpStatus: res.status,
          statusText: res.statusText,
          durationMs: latencyMs,
          errorMessage: `HTTP ${res.status}: ${res.statusText}`,
          detailedReason: reason,
          responseSnippet: text.substring(0, 150)
        });
        return { ok: false, latencyMs, error: `HTTP ${res.status}`, details: reason };
      }
    } catch (err: any) {
      const latencyMs = Math.round(performance.now() - startTime);
      const reason = this.analyzeNetworkError(err);
      this.updateLog(log.id, {
        status: 'failed',
        durationMs: latencyMs,
        errorMessage: err.message || 'Network failure',
        detailedReason: reason
      });
      return { ok: false, latencyMs, error: err.message || 'Connection failed', details: reason };
    }
  }

  private analyzeHttpFailure(status: number, responseText: string): string {
    if (status === 402 || responseText.includes('402 Payment Required') || responseText.includes('"status":402')) {
      return 'Payment Required: Pollinations legacy API now requires credits for specified model. (Auto-mitigating to free default cluster)';
    }
    if (status === 429 || responseText.includes('Queue full') || responseText.includes('"status":429')) {
      return 'Rate Limited: Pollinations free public IP queue is currently congested. (Auto-mitigated via GET fallback)';
    }
    if (status === 403) {
      return 'Forbidden: Cloudflare / WAF blocked direct request or IP restricted.';
    }
    if (status >= 500) {
      return 'Upstream Outage: Pollinations AI inference servers are undergoing maintenance or temporary load spikes.';
    }
    return `Server responded with status HTTP ${status}: ${responseText.substring(0, 100)}`;
  }

  private analyzeNetworkError(err: any): string {
    const msg = (err?.message || '').toLowerCase();
    if (msg.includes('abort') || msg.includes('timeout')) {
      return 'Request Timed Out: Inference took longer than timeout threshold due to queue congestion.';
    }
    if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed')) {
      return 'Browser Network Block: Fetch blocked. Most likely caused by AdBlocker / Brave Shields or offline connection.';
    }
    return `Network communication exception: ${err.message || 'Unknown network error'}`;
  }

  async generate(userPrompt: string): Promise<{ text: string; blueprint: MiniAppBlueprint; sourceUsed: string }> {
    this.history.push({
      role: 'user',
      content: userPrompt,
      timestamp: Date.now()
    });

    const currentBp = this.getCurrentBlueprint();
    const currentBpStr = JSON.stringify(currentBp, null, 2);
    
    // Combine context into user prompt to avoid Pollinations 402 role:system restriction on free endpoints
    const contextualUserPrompt = `[Telegram Mini App Architect Instructions]:
You are designing Telegram Mini Apps. The user wants to update or create an app.
Current App Blueprint:
${currentBpStr}

User Request: "${userPrompt}"

Please provide a concise, friendly response explaining the mini-app concept and changes, followed by the complete updated JSON blueprint inside a \`\`\`json ... \`\`\` code block.`;

    let responseText = '';
    let usedAiSource = '';

    // STAGE 0: Puter.js Browser AI Engine (if loaded in window)
    if (typeof (window as any).puter !== 'undefined' && (window as any).puter.ai) {
      const puterStart = performance.now();
      const puterLog = this.addLog({
        timestamp: Date.now(),
        endpoint: 'puter://ai.chat',
        method: 'JS-SDK',
        model: 'puter-ai-browser',
        status: 'pending',
        stage: 'Stage 0: Puter.js In-Browser AI Engine',
        payloadSummary: `Prompt: "${userPrompt.substring(0, 60)}..."`
      });

      try {
        const puterRes = await Promise.race([
          (window as any).puter.ai.chat(contextualUserPrompt),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Puter AI timeout (5s)')), 5000))
        ]);

        const durationMs = Math.round(performance.now() - puterStart);
        let extractedText = '';
        if (typeof puterRes === 'string') {
          extractedText = puterRes;
        } else if (puterRes?.message?.content) {
          extractedText = puterRes.message.content;
        } else if (puterRes?.toString) {
          extractedText = puterRes.toString();
        }

        if (extractedText && extractedText.trim().length > 10) {
          responseText = extractedText;
          usedAiSource = 'Puter.js Browser AI Engine (Client SDK)';
          this.updateLog(puterLog.id, {
            status: 'success',
            durationMs,
            responseSnippet: extractedText.substring(0, 180)
          });
        } else {
          this.updateLog(puterLog.id, {
            status: 'failed',
            durationMs,
            detailedReason: 'Puter returned empty response, falling back to Pollinations'
          });
        }
      } catch (errPuter: any) {
        const durationMs = Math.round(performance.now() - puterStart);
        this.updateLog(puterLog.id, {
          status: 'failed',
          durationMs,
          errorMessage: errPuter.message || 'Puter error',
          detailedReason: `Puter.js SDK call bypassed: ${errPuter.message || 'Network/auth required'}. Fallback engaged.`
        });
      }
    }

    // Helper for fetch with timeout (5000ms for fast feedback)
    const fetchWithTimeout = async (url: string, options: RequestInit, toMs = 5000): Promise<Response> => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), toMs);
      try {
        const res = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(id);
        return res;
      } catch (err) {
        clearTimeout(id);
        throw err;
      }
    };

    // Determine active model to use
    const userChosenModel = this.config.preferredModel?.trim() || '';

    // STAGE 1: Standard POST to https://text.pollinations.ai/
    if (!responseText) {
      const postEndpoint = this.config.customEndpoint || 'https://text.pollinations.ai/';
      const postLog = this.addLog({
        timestamp: Date.now(),
        endpoint: postEndpoint,
        method: 'POST',
        model: userChosenModel || 'default (free auto-cluster)',
        status: 'pending',
        stage: 'Stage 1: Primary Browser AI POST (text.pollinations.ai)',
        payloadSummary: `Prompt: "${userPrompt.substring(0, 60)}..."`
      });

      const postStart = performance.now();
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'Accept': 'text/plain, application/json'
        };
        if (this.config.apiKey) {
          headers['Authorization'] = `Bearer ${this.config.apiKey}`;
        }

        const bodyData: any = {
          messages: [
            {
              role: 'user',
              content: contextualUserPrompt
            }
          ],
          seed: Math.floor(Math.random() * 1000000)
        };
        if (userChosenModel) {
          bodyData.model = userChosenModel;
        }

        const response = await fetchWithTimeout(postEndpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(bodyData)
        }, 5000);

        const durationMs = Math.round(performance.now() - postStart);

        if (response.ok) {
          const raw = await response.text();
          if (raw && !raw.includes('"status":429') && !raw.includes('"status":402') && !raw.includes('402 Payment Required') && !raw.includes('Queue full')) {
            let extractedContent = raw;
            try {
              const jsonParsed = JSON.parse(raw);
              if (jsonParsed.choices && jsonParsed.choices[0]?.message?.content) {
                extractedContent = jsonParsed.choices[0].message.content;
              }
            } catch {
              // raw text
            }

            responseText = extractedContent;
            usedAiSource = `Pollinations Browser AI (POST / ${userChosenModel || 'Default Free Cluster'})`;
            this.updateLog(postLog.id, {
              status: 'success',
              httpStatus: response.status,
              statusText: response.statusText,
              durationMs,
              responseSnippet: extractedContent.substring(0, 180)
            });
          } else {
            const reason = this.analyzeHttpFailure(response.status, raw);
            this.updateLog(postLog.id, {
              status: 'failed',
              httpStatus: response.status,
              statusText: response.statusText,
              durationMs,
              detailedReason: reason,
              responseSnippet: raw.substring(0, 150)
            });
          }
        } else {
          const errText = await response.text();
          const reason = this.analyzeHttpFailure(response.status, errText);
          this.updateLog(postLog.id, {
            status: 'failed',
            httpStatus: response.status,
            statusText: response.statusText,
            durationMs,
            errorMessage: `HTTP ${response.status}: ${response.statusText}`,
            detailedReason: reason,
            responseSnippet: errText.substring(0, 150)
          });
        }
      } catch (e1: any) {
        const durationMs = Math.round(performance.now() - postStart);
        const reason = this.analyzeNetworkError(e1);
        this.updateLog(postLog.id, {
          status: 'failed',
          durationMs,
          errorMessage: e1.message || 'POST fetch failed',
          detailedReason: reason
        });
      }
    }

    // STAGE 2: Direct Browser GET Query (identical to browser address bar query)
    if (!responseText) {
      const getStart = performance.now();
      const directPrompt = `Telegram Mini App Architect: User says "${userPrompt}". Current App is "${currentBp.name}". Provide concise summary and full updated JSON blueprint inside \`\`\`json block.`;
      const cleanPrompt = encodeURIComponent(directPrompt);
      const getUrl = userChosenModel 
        ? `https://text.pollinations.ai/${cleanPrompt}?model=${encodeURIComponent(userChosenModel)}`
        : `https://text.pollinations.ai/${cleanPrompt}`;

      const getLog = this.addLog({
        timestamp: Date.now(),
        endpoint: getUrl.substring(0, 80) + '...',
        method: 'GET',
        model: userChosenModel || 'default (auto)',
        status: 'pending',
        stage: 'Stage 2: Direct Browser GET Query (Zero Parameter Default)',
        payloadSummary: `GET query for prompt: "${userPrompt.substring(0, 60)}..."`
      });

      try {
        const getRes = await fetchWithTimeout(getUrl, {
          method: 'GET',
          headers: { 'Accept': 'text/plain, application/json' }
        }, 5000);

        const durationMs = Math.round(performance.now() - getStart);

        if (getRes.ok) {
          const raw = await getRes.text();
          if (raw && !raw.includes('"status":429') && !raw.includes('"status":402') && !raw.includes('Queue full')) {
            responseText = raw;
            usedAiSource = `Pollinations Browser AI (Direct GET / ${userChosenModel || 'Default Free Cluster'})`;
            this.updateLog(getLog.id, {
              status: 'success',
              httpStatus: getRes.status,
              statusText: getRes.statusText,
              durationMs,
              responseSnippet: raw.substring(0, 180)
            });
          } else {
            this.updateLog(getLog.id, {
              status: 'failed',
              httpStatus: getRes.status,
              durationMs,
              detailedReason: this.analyzeHttpFailure(getRes.status, raw)
            });
          }
        } else {
          const rawErr = await getRes.text();
          this.updateLog(getLog.id, {
            status: 'failed',
            httpStatus: getRes.status,
            durationMs,
            detailedReason: this.analyzeHttpFailure(getRes.status, rawErr)
          });
        }
      } catch (e2: any) {
        const durationMs = Math.round(performance.now() - getStart);
        this.updateLog(getLog.id, {
          status: 'failed',
          durationMs,
          errorMessage: e2.message,
          detailedReason: this.analyzeNetworkError(e2)
        });
      }
    }

    // Parse Blueprint with resilient JSON repair
    let extractedBlueprint: MiniAppBlueprint | null = null;
    if (responseText) {
      extractedBlueprint = this.parseBlueprintFromResponse(responseText);
    }

    // STAGE 3: Client Neural Heuristic Generator if API was unreachable or unparsable
    if (!extractedBlueprint) {
      const syn = this.applyLocalModifications(userPrompt, currentBp);
      extractedBlueprint = syn;
      usedAiSource = 'Client Neural Architect Engine (Zero-Latency Mitigation)';
      
      this.addLog({
        timestamp: Date.now(),
        endpoint: 'client://neural-heuristic-engine',
        method: 'POST',
        model: 'client-offline-heuristic',
        status: 'success',
        stage: 'Stage 3: Client Neural Fallback (Automatic Mitigation)',
        payloadSummary: `Synthesized blueprint locally for "${userPrompt.substring(0, 60)}"`,
        responseSnippet: `Applied modifications for: ${syn.name} (${syn.template_id})`,
        detailedReason: 'Pollinations cluster latency or network restriction encountered. Local neural engine generated the full Telegram Mini-App blueprint immediately.'
      });

      if (!responseText) {
        responseText = this.generateConversationalResponse(userPrompt, extractedBlueprint);
      }
    }

    this.currentBlueprint = extractedBlueprint;

    // Clean conversational text of raw JSON block for cleaner UI rendering
    const cleanText = responseText.replace(/```json[\s\S]*?```/gi, '').trim() || 
      `I have synthesized your **${extractedBlueprint.name}** Telegram Mini-App blueprint!`;

    // Save assistant message in history
    this.history.push({
      role: 'assistant',
      content: cleanText,
      timestamp: Date.now(),
      blueprint: extractedBlueprint,
      source: usedAiSource as any
    });

    return {
      text: cleanText,
      blueprint: extractedBlueprint,
      sourceUsed: usedAiSource
    };
  }

  private generateConversationalResponse(userPrompt: string, bp: MiniAppBlueprint): string {
    const p = userPrompt.toLowerCase().trim();
    if (p === 'hi' || p === 'hello' || p === 'hey' || p === 'help' || p === 'start' || p === 'yo') {
      return `### 👋 Welcome to AI Mini-App Architect!\n\nI'm your dedicated Telegram Mini App engineer. You currently have **${bp.name}** loaded in the sandbox simulator.\n\nHere are a few things you can tell me to create or customize:\n\n- **"Create a TON Tap-to-Earn mini-app with 500 coin bonus"**\n- **"Build a gold luxury staking portal called SparkGold"**\n- **"Change theme to cyber dark neon purple with 5 referral tasks"**\n- **"Set currency to USDT and enable daily streak bonus"**\n\nWhat kind of earning app or game would you like to build?`;
    }

    return `### ⚡ ${bp.name} Blueprint Configured\n\nI have adapted your **${bp.name}** Telegram Mini-App blueprint according to your request (**"${userPrompt}"**):\n\n- **Template Archetype**: \`${bp.template_id}\`\n- **Tokenomics**: **${bp.currency_name}** with **+${bp.welcome_bonus}** sign-up grant and **+${bp.referral_reward}** viral invite multiplier.\n- **Theme Palette**: Primary \`${bp.theme.primaryColor}\`, Accent \`${bp.theme.accentColor}\`, and Canvas \`${bp.theme.bgColor}\`.\n- **Withdrawal Minimum**: **${bp.min_withdrawal} ${bp.currency_name}**\n\nCheck out the real-time device mockup on the right, or click **Auto-Host & Launch App** to deploy!`;
  }

  private applyLocalModifications(prompt: string, prev: MiniAppBlueprint): MiniAppBlueprint {
    const p = prompt.toLowerCase();
    const updated: MiniAppBlueprint = {
      ...prev,
      theme: { ...prev.theme },
      loading_config: { ...prev.loading_config },
      tabs_config: [...(prev.tabs_config || [])],
      initial_tasks: [...(prev.initial_tasks || [])]
    };

    // If it's just a greeting, preserve current blueprint without unintended overwrites
    if (p === 'hi' || p === 'hello' || p === 'hey' || p === 'help' || p === 'start' || p === 'yo') {
      return updated;
    }

    // Template detection
    if (p.includes('earnhub') || p.includes('mission') || p.includes('recruitment') || p.includes('referral') || p.includes('quest')) {
      updated.template_id = 'earnhub';
      updated.icon = 'target';
    } else if (p.includes('bnb') || p.includes('spark') || p.includes('stake') || p.includes('gold') || p.includes('yield')) {
      updated.template_id = 'sparkbnb';
      updated.icon = 'crown';
    } else if (p.includes('bux') || p.includes('dux') || p.includes('ad') || p.includes('streak') || p.includes('watch')) {
      updated.template_id = 'duxbux';
      updated.icon = 'flame';
    } else if (p.includes('crypto') || p.includes('spin') || p.includes('hybrid') || p.includes('wheel') || p.includes('airdrop')) {
      updated.template_id = 'hybrid_rewards_x';
      updated.icon = 'zap';
    }

    // Currency detection
    if (p.includes('ton')) updated.currency_name = 'TON';
    else if (p.includes('usdt')) updated.currency_name = 'USDT';
    else if (p.includes('not')) updated.currency_name = 'NOT';
    else if (p.includes('dogs')) updated.currency_name = 'DOGS';
    else if (p.includes('bnb')) updated.currency_name = 'BNB';
    else if (p.includes('sol') || p.includes('solana')) updated.currency_name = 'SOL';
    else if (p.includes('btc') || p.includes('bitcoin')) updated.currency_name = 'BTC';
    else if (p.includes('gem') || p.includes('diamond')) updated.currency_name = 'GEMS';
    else if (p.includes('coin') || p.includes('token')) updated.currency_name = 'COINS';
    else if (p.includes('points') || p.includes('pts')) updated.currency_name = 'POINTS';

    // Theme detection
    if (p.includes('dark') || p.includes('black') || p.includes('cyber') || p.includes('neon') || p.includes('matrix')) {
      updated.theme.bgColor = '#050811';
      updated.theme.primaryColor = '#00f0ff';
      updated.theme.secondaryColor = '#7000ff';
      updated.theme.accentColor = '#ff0055';
      updated.theme.cardColor = 'rgba(0, 240, 255, 0.06)';
    } else if (p.includes('gold') || p.includes('yellow') || p.includes('luxury') || p.includes('amber')) {
      updated.theme.bgColor = '#0e0e11';
      updated.theme.primaryColor = '#f59e0b';
      updated.theme.secondaryColor = '#fbbf24';
      updated.theme.accentColor = '#3b82f6';
      updated.theme.cardColor = 'rgba(251, 191, 36, 0.06)';
    } else if (p.includes('green') || p.includes('emerald')) {
      updated.theme.bgColor = '#04100b';
      updated.theme.primaryColor = '#10b981';
      updated.theme.secondaryColor = '#34d399';
      updated.theme.accentColor = '#c0ff4d';
      updated.theme.cardColor = 'rgba(168, 85, 247, 0.06)';
    } else if (p.includes('purple') || p.includes('violet')) {
      updated.theme.bgColor = '#0c0718';
      updated.theme.primaryColor = '#a855f7';
      updated.theme.secondaryColor = '#ec4899';
      updated.theme.accentColor = '#06b6d4';
      updated.theme.cardColor = 'rgba(168, 85, 247, 0.06)';
    } else if (p.includes('blue') || p.includes('ocean')) {
      updated.theme.bgColor = '#060f1e';
      updated.theme.primaryColor = '#3b82f6';
      updated.theme.secondaryColor = '#60a5fa';
      updated.theme.accentColor = '#38bdf8';
      updated.theme.cardColor = 'rgba(59, 130, 246, 0.06)';
    }

    // Name extraction
    const namedMatch = prompt.match(/(?:called|name(?:d)?|named|app\s+name|create\s+([A-Za-z0-9\s]+?)(?:\s+app|\s+with|\s+for|$))/i);
    if (namedMatch && (namedMatch[1] || namedMatch[0])) {
      const extracted = (namedMatch[1] || namedMatch[0]).replace(/^(called|named|name|create)\s+/i, '').trim();
      if (extracted.length > 2 && !['a', 'an', 'the', 'my', 'new'].includes(extracted.toLowerCase())) {
        updated.name = extracted.substring(0, 26);
      }
    }

    // Numbers (bonus/referral)
    const bonusMatch = prompt.match(/(\d+)\s*(?:bonus|welcome|coin|start|signup)/i);
    if (bonusMatch && bonusMatch[1]) {
      updated.welcome_bonus = parseInt(bonusMatch[1]);
    }
    const refMatch = prompt.match(/(\d+)\s*(?:ref|referral|invite|friend)/i);
    if (refMatch && refMatch[1]) {
      updated.referral_reward = parseInt(refMatch[1]);
    }

    // Tasks
    if (p.includes('task') || p.includes('mission') || p.includes('quest')) {
      updated.initial_tasks = [
        {
          title: `Join ${updated.name} Telegram Announcement Channel`,
          description: 'Official channel verification for instant reward bonus',
          reward: Math.floor(updated.welcome_bonus * 0.5) || 50,
          link: 'https://t.me/telegram',
          icon: '📢',
          category: 'Telegram'
        },
        {
          title: `Follow ${updated.name} on X (Twitter)`,
          description: 'Follow and like our pinned post for airdrop credit',
          reward: Math.floor(updated.welcome_bonus * 0.75) || 75,
          link: 'https://x.com',
          icon: '🐦',
          category: 'Social'
        },
        {
          title: 'Invite 3 Active Friends',
          description: 'Share your personal referral link to unlock withdrawal tier',
          reward: updated.referral_reward * 3 || 150,
          link: '',
          icon: '👥',
          category: 'Viral'
        },
        {
          title: 'Daily Streak Bonus Claim',
          description: 'Check in every 24 hours to multiply your mining velocity',
          reward: Math.floor(updated.welcome_bonus * 0.25) || 25,
          link: '',
          icon: '🔥',
          category: 'Daily'
        }
      ];
    }

    return updated;
  }

  // Resilient JSON parser that handles markdown wrapping, unclosed quotes, and trailing commas
  private parseBlueprintFromResponse(text: string): MiniAppBlueprint | null {
    try {
      // 1. Try markdown ```json ... ``` extraction
      let rawJson = '';
      const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
      if (codeBlockMatch && codeBlockMatch[1]) {
        rawJson = codeBlockMatch[1].trim();
      } else {
        // 2. Try raw object bracket matching
        const bracketMatch = text.match(/\{[\s\S]*"name"[\s\S]*"currency_name"[\s\S]*\}/i) ||
                             text.match(/\{[\s\S]*"template_id"[\s\S]*\}/i);
        if (bracketMatch) {
          rawJson = bracketMatch[0].trim();
        }
      }

      if (!rawJson) return null;

      // Resilient sanitization
      // Remove trailing commas in objects and arrays
      let sanitized = rawJson
        .replace(/,\s*([\}\]])/g, '$1')
        .replace(/[\u201C\u201D]/g, '"') // Replace smart quotes
        .replace(/[\u2018\u2019]/g, "'");

      // Check if closing brace is missing
      if (!sanitized.endsWith('}')) {
        const openBraces = (sanitized.match(/\{/g) || []).length;
        const closeBraces = (sanitized.match(/\}/g) || []).length;
        if (openBraces > closeBraces) {
          sanitized += '}'.repeat(openBraces - closeBraces);
        }
      }

      const parsed = JSON.parse(sanitized);
      if (parsed && (parsed.name || parsed.currency_name || parsed.template_id)) {
        const def = this.getDefaultBlueprint();
        return {
          name: parsed.name || def.name,
          currency_name: parsed.currency_name || def.currency_name,
          template_id: parsed.template_id || def.template_id,
          icon: parsed.icon || def.icon,
          welcome_bonus: Number(parsed.welcome_bonus) || def.welcome_bonus,
          referral_reward: Number(parsed.referral_reward) || def.referral_reward,
          theme: {
            primaryColor: parsed.theme?.primaryColor || def.theme.primaryColor,
            secondaryColor: parsed.theme?.secondaryColor || def.theme.secondaryColor,
            accentColor: parsed.theme?.accentColor || def.theme.accentColor,
            bgColor: parsed.theme?.bgColor || def.theme.bgColor,
            cardColor: parsed.theme?.cardColor || def.theme.cardColor,
            fontFamily: parsed.theme?.fontFamily || def.theme.fontFamily,
            loadingGradient: parsed.theme?.loadingGradient || def.theme.loadingGradient
          },
          loading_config: {
            text: parsed.loading_config?.text || def.loading_config.text,
            color: parsed.loading_config?.color || def.loading_config.color
          },
          conversion_enabled: parsed.conversion_enabled ?? def.conversion_enabled,
          conversion_rate: Number(parsed.conversion_rate) || def.conversion_rate,
          conversion_currency: parsed.conversion_currency || def.conversion_currency,
          min_withdrawal: Number(parsed.min_withdrawal) || def.min_withdrawal,
          mandatory_tasks_to_withdraw: Number(parsed.mandatory_tasks_to_withdraw) || def.mandatory_tasks_to_withdraw,
          tabs_config: Array.isArray(parsed.tabs_config) && parsed.tabs_config.length > 0
            ? parsed.tabs_config
            : def.tabs_config,
          initial_tasks: Array.isArray(parsed.initial_tasks) && parsed.initial_tasks.length > 0 
            ? parsed.initial_tasks 
            : def.initial_tasks
        };
      }
    } catch (e) {
      console.warn('JSON parsing attempt notice:', e);
    }
    return null;
  }
}

export const aiArchitect = new AiArchitectService();
